import { useABAC } from "@adornis/abac/client/useABAC.js";
import {
  createPermissionPayload,
  type PermissionContext,
  Role,
  RoleAssignment,
} from "@adornis/abac/policies/SimpleRolePolicy.js";
import { subscribeAllEntities } from "@adornis/baseql/operations/mongo.js";
import { guardDialog } from "@adornis/dialog/dialog-functions.js";
import { DropdownSelectionInput } from "@adornis/formfield/fields/dropdown-selection-input.js";
import { stateful, useState } from "@adornis/functional-lit/stateful.js";
import { useQuery } from "@adornis/functional-lit/use-query.js";
import { goTo } from "@adornis/router/client/open-href.js";
import { AdornisUser } from "@adornis/users/db/a-user.js";
import { html, nothing } from "lit";
import { SmartTeamsUserProfile } from "../../db/SmartTeamsUserProfile.js";
import { accessController } from "../../features/permissions/init-permissions.js";
import {
  SmartTeamsPermission,
  SmartTeamsRoleNames,
} from "../../features/permissions/roles-and-permissions.js";
import {
  addCompanyToScope,
  hasCompanyInScope,
  removeCompanyFromScope,
} from "../../features/permissions/scope-helpers.js";

export const LinkedPartnerManagers = stateful(
  ({ companyId }: { companyId: string }) => {
    const { can: canManageUsers } = useABAC(
      accessController,
      createPermissionPayload(SmartTeamsPermission.UserManage),
    );
    const [refreshKey, setRefreshKey] = useState(0);

    // Load all roles to find Partner Manager role
    const rolesQuery = useQuery(
      () => subscribeAllEntities(Role)({ _id: 1, roleName: 1 }),
      [],
    );

    // Load all role assignments with scope (new structure: roleIds array)
    const roleAssignmentsQuery = useQuery(
      () =>
        subscribeAllEntities(RoleAssignment)({
          _id: 1,
          userId: 1,
          roleIds: 1,
          scope: { contexts: 1, permission: 1 },
        }),
      [refreshKey],
    );

    // Load all user profiles (for displayName only)
    const profilesQuery = useQuery(
      () =>
        subscribeAllEntities(SmartTeamsUserProfile)({
          _id: 1,
          userId: 1,
          displayName: 1,
        }),
      [],
    );

    // Load all users
    const usersQuery = useQuery(
      () =>
        subscribeAllEntities(AdornisUser)({ _id: 1, email: 1, username: 1 }),
      [],
    );

    const roles = rolesQuery.value ?? [];
    const roleAssignments = roleAssignmentsQuery.value ?? [];
    const profiles = profilesQuery.value ?? [];
    const users = usersQuery.value ?? [];

    // Find Partner Manager role
    const partnerManagerRole = roles.find(
      (r) => r.roleName === SmartTeamsRoleNames.PARTNER_MANAGER,
    );
    const partnerManagerRoleId = partnerManagerRole?._id;

    // Get Partner Manager assignments that have this company in their scope
    const linkedPMAssignments = roleAssignments.filter(
      (ra) =>
        partnerManagerRoleId &&
        ra.roleIds?.includes(partnerManagerRoleId) &&
        hasCompanyInScope(
          (ra.scope ?? undefined) as PermissionContext[] | undefined,
          companyId,
        ),
    );

    // Map assignments to users
    const linkedUsers = linkedPMAssignments
      .map((assignment) => {
        const user = users.find((u) => u._id === assignment.userId);
        const profile = profiles.find((p) => p.userId === assignment.userId);
        return user ? { user, profile, assignment } : null;
      })
      .filter(Boolean) as Array<{
      user: AdornisUser<any>;
      profile?: SmartTeamsUserProfile;
      assignment: RoleAssignment;
    }>;

    // Get available Partner Managers (have PM role but not assigned to this company)
    const linkedUserIds = new Set(linkedUsers.map((lu) => lu.user._id));
    const availablePartnerManagers = roleAssignments
      .filter(
        (ra) =>
          partnerManagerRoleId &&
          ra.roleIds?.includes(partnerManagerRoleId) &&
          !linkedUserIds.has(ra.userId),
      )
      .map((assignment) => {
        const user = users.find((u) => u._id === assignment.userId);
        const profile = profiles.find((p) => p.userId === assignment.userId);
        return user ? { user, profile, assignment } : null;
      })
      .filter(Boolean) as Array<{
      user: AdornisUser<any>;
      profile?: SmartTeamsUserProfile;
      assignment: RoleAssignment;
    }>;

    const refresh = () => setRefreshKey(refreshKey + 1);

    const navigateToUser = (userId: string) => {
      if (canManageUsers) {
        void goTo(`/users/${userId}`);
      }
    };

    const handleAddPartnerManager = async (
      selection: {
        user: AdornisUser<any>;
        profile?: SmartTeamsUserProfile;
        assignment: RoleAssignment;
      }[],
    ) => {
      if (!selection.length) return;

      await guardDialog(
        async () => {
          for (const { assignment } of selection) {
            assignment.scope = addCompanyToScope(
              assignment.scope ?? undefined,
              companyId,
            );
            await assignment.save?.();
          }
        },
        {
          loadingText: "Partner Manager is being added…",
          successText: "Partner Manager added.",
          errorMessagePrefix: "Partner Manager could not be added",
        },
      );
      refresh();
    };

    const handleRemovePartnerManager = async (assignment: RoleAssignment) => {
      await guardDialog(
        async () => {
          assignment.scope = removeCompanyFromScope(
            assignment.scope ?? undefined,
            companyId,
          );
          await assignment.save?.();
        },
        {
          loadingText: "Partner Manager is being removed…",
          successText: "Partner Manager removed.",
          errorMessagePrefix: "Partner Manager could not be removed",
        },
      );
      refresh();
    };

    const isLoading =
      profilesQuery.loading ||
      usersQuery.loading ||
      rolesQuery.loading ||
      roleAssignmentsQuery.loading;

    if (isLoading) {
      return html`<span
        class="loading loading-spinner loading-sm text-primary"
      ></span>`;
    }

    return html`
      <div class="flex flex-col gap-3">
        <!-- Assigned Partner Managers -->
        <div class="flex flex-wrap gap-2">
          ${linkedUsers.length === 0
            ? html`<p class="text-sm text-base-content/50">
                No Partner Manager assigned
              </p>`
            : linkedUsers.map(
                ({ user, profile, assignment }) => html`
                  <div
                    class="badge badge-lg gap-2 py-3 pr-1 ${canManageUsers
                      ? "cursor-pointer hover:badge-primary"
                      : ""}"
                  >
                    <div
                      class="flex items-center gap-2"
                      @click=${(e: Event) => {
                        e.stopPropagation();
                        navigateToUser(user._id);
                      }}
                    >
                      <div class="avatar placeholder">
                        <div
                          class="bg-primary text-primary-content rounded-full w-5 h-5 flex items-center justify-center"
                        >
                          <span class="text-[10px] font-bold"
                            >${(user.email ??
                              user.username ??
                              "?")[0]?.toUpperCase() ?? "?"}</span
                          >
                        </div>
                      </div>
                      <span
                        >${profile?.displayName ??
                        user.email ??
                        user.username}</span
                      >
                    </div>
                    ${canManageUsers
                      ? html`
                          <button
                            class="btn btn-ghost btn-xs btn-circle"
                            @click=${(e: Event) => {
                              e.stopPropagation();
                              void handleRemovePartnerManager(assignment);
                            }}
                          >
                            <span class="icon-[mdi--close] text-sm"></span>
                          </button>
                        `
                      : nothing}
                  </div>
                `,
              )}
        </div>

        <!-- Add Partner Manager Dropdown -->
        ${canManageUsers && availablePartnerManagers.length > 0
          ? html`
              <div class="w-64">
                ${DropdownSelectionInput<
                  {
                    user: AdornisUser<any>;
                    profile?: SmartTeamsUserProfile;
                    assignment: RoleAssignment;
                  },
                  false
                >({
                  multi: false,
                  placeholder: "Add Partner Manager",
                  selectables: availablePartnerManagers,
                  value: undefined,
                  renderString: (item) =>
                    item.user.email ?? item.user.username ?? item.user._id,
                  resolveID: (item) => item.user._id,
                  emptyState: "No available Partner Manager",
                  onPicked: (value) => {
                    if (value) {
                      void handleAddPartnerManager([value]);
                    }
                  },
                })}
              </div>
            `
          : canManageUsers &&
              availablePartnerManagers.length === 0 &&
              linkedUsers.length > 0
            ? nothing
            : canManageUsers
              ? html`<p class="text-xs text-base-content/50">
                  No further Partner Manager available. Assign users the
                  Partner Manager role first.
                </p>`
              : nothing}
      </div>
    `;
  },
);
